import React, { useEffect, useState } from "react";
import { Sparkles, AlertTriangle, Lightbulb, Loader2, RefreshCw, MessageSquare, Copy, Check, Send, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function AIInsightsCard({ accentColor = "#EA580C", role = "auto" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);

  const load = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get("/ai/insights");
      setData(res);
    } catch (e) {
      setError("Impossible de charger les insights IA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyRelance = async () => {
    if (!data?.relance_template) return;
    await navigator.clipboard.writeText(data.relance_template);
    setCopied(true);
    toast.success("Modèle copié — collez-le dans WhatsApp / SMS");
    setTimeout(() => setCopied(false), 2000);
  };

  // Nettoie un numéro guinéen pour wa.me (format E.164 sans +)
  const cleanPhone = (raw) => {
    if (!raw) return "";
    let p = String(raw).replace(/\D/g, "");
    if (p.startsWith("00")) p = p.slice(2);
    if (!p.startsWith("224") && p.length === 9) p = "224" + p;
    return p;
  };

  const sendWhatsApp = (parentPhone, parentName) => {
    if (!data?.relance_template) {
      toast.error("Aucun modèle de relance disponible");
      return;
    }
    const phone = cleanPhone(parentPhone);
    if (!phone) {
      toast.error("Numéro de téléphone du parent manquant");
      return;
    }
    // Personnalise légèrement le message avec le nom du parent
    let message = data.relance_template;
    if (parentName && !message.includes(parentName)) {
      message = message.replace(/^(Bonjour[^,\n]*[,]?)/i, `Bonjour ${parentName},`);
    }
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(`WhatsApp ouvert pour ${parentName || phone}`);
    setShowRecipients(false);
  };

  // Liste des parents à relancer depuis le contexte (finance)
  const recipients = (data?.context?.top_unpaid || []).filter(u => u.parent_phone);

  return (
    <div className="relative bg-gradient-to-br from-white via-orange-50/40 to-white border border-slate-200 rounded-2xl p-6 overflow-hidden" data-testid="ai-insights-card">
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${accentColor}, transparent 70%)` }} />

      <div className="relative flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` }}>
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">MAZID AI · Synthèse intelligente</div>
            <div className="font-heading font-extrabold text-lg text-[#1E3A8A]">Vos priorités du jour</div>
          </div>
        </div>
        <button onClick={() => load(true)} disabled={loading} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md text-slate-600 hover:bg-slate-100 disabled:opacity-50" data-testid="ai-insights-refresh">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Actualiser
        </button>
      </div>

      {loading && !data && (
        <div className="py-10 text-center text-slate-500 text-sm">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3 text-[#EA580C]" />
          MAZID AI analyse vos données réelles...
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="ai-insights-error">{error}</div>
      )}

      {data && (
        <div className="relative space-y-4">
          {/* Résumé */}
          <div className="bg-white/70 backdrop-blur rounded-xl p-4 border border-slate-100">
            <div className="text-xs font-bold uppercase tracking-wider text-[#166534] mb-2">📊 Résumé</div>
            <p className="text-sm text-slate-700 leading-relaxed" data-testid="ai-insights-summary">{data.summary}</p>
          </div>

          {/* Alertes */}
          {Array.isArray(data.alerts) && data.alerts.length > 0 && (
            <div className="bg-orange-50/60 rounded-xl p-4 border border-orange-200/60">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-[#EA580C]" />
                <div className="text-xs font-bold uppercase tracking-wider text-[#EA580C]">Alertes prioritaires</div>
              </div>
              <ul className="space-y-1.5">
                {data.alerts.map((a, i) => (
                  <li key={i} className="text-sm text-slate-700 flex gap-2 items-start" data-testid={`ai-alert-${i}`}>
                    <span className="text-[#EA580C] font-bold flex-shrink-0">•</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommandations */}
          {Array.isArray(data.recommendations) && data.recommendations.length > 0 && (
            <div className="bg-green-50/60 rounded-xl p-4 border border-green-200/60">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="h-4 w-4 text-[#166534]" />
                <div className="text-xs font-bold uppercase tracking-wider text-[#166534]">Actions recommandées</div>
              </div>
              <ul className="space-y-1.5">
                {data.recommendations.map((r, i) => (
                  <li key={i} className="text-sm text-slate-700 flex gap-2 items-start" data-testid={`ai-reco-${i}`}>
                    <span className="text-[#166534] font-bold flex-shrink-0">{i + 1}.</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Modèle de relance + bouton WhatsApp (finance) */}
          {data.relance_template && (
            <div className="bg-[#1E3A8A]/5 rounded-xl p-4 border border-[#1E3A8A]/20">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-[#1E3A8A]" />
                  <div className="text-xs font-bold uppercase tracking-wider text-[#1E3A8A]">Modèle de relance WhatsApp</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={copyRelance} className="text-xs flex items-center gap-1 px-2.5 py-1.5 rounded bg-white border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="copy-relance-btn">
                    {copied ? <><Check className="h-3 w-3" /> Copié</> : <><Copy className="h-3 w-3" /> Copier</>}
                  </button>

                  {/* Bouton WhatsApp principal — avec menu déroulant si plusieurs parents */}
                  {recipients.length === 0 ? (
                    <button disabled className="text-xs flex items-center gap-1 px-3 py-1.5 rounded bg-slate-200 text-slate-400 cursor-not-allowed" data-testid="whatsapp-send-disabled">
                      <Send className="h-3 w-3" /> WhatsApp indisponible
                    </button>
                  ) : recipients.length === 1 ? (
                    <button
                      onClick={() => sendWhatsApp(recipients[0].parent_phone, recipients[0].parent)}
                      className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#25D366] text-white hover:bg-[#1da851] font-semibold shadow-sm"
                      data-testid="whatsapp-send-btn"
                    >
                      <Send className="h-3 w-3" /> Envoyer à {recipients[0].parent}
                    </button>
                  ) : (
                    <div className="relative">
                      <button
                        onClick={() => setShowRecipients(s => !s)}
                        className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#25D366] text-white hover:bg-[#1da851] font-semibold shadow-sm"
                        data-testid="whatsapp-send-btn"
                      >
                        <Send className="h-3 w-3" /> Envoyer par WhatsApp <ChevronDown className="h-3 w-3" />
                      </button>
                      {showRecipients && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowRecipients(false)} />
                          <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-slate-200 z-20 overflow-hidden" data-testid="whatsapp-recipients-menu">
                            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Choisir un destinataire</div>
                            </div>
                            <div className="max-h-80 overflow-y-auto">
                              {recipients.map((r, i) => (
                                <button
                                  key={i}
                                  onClick={() => sendWhatsApp(r.parent_phone, r.parent)}
                                  className="w-full px-4 py-3 text-left hover:bg-green-50 transition flex items-center justify-between gap-2 border-b border-slate-100 last:border-0"
                                  data-testid={`whatsapp-recipient-${i}`}
                                >
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold text-[#1E3A8A] truncate">{r.parent}</div>
                                    <div className="text-[11px] text-slate-500 truncate">{r.student} · {r.class}</div>
                                    <div className="text-[11px] text-slate-400">{r.parent_phone}</div>
                                  </div>
                                  <div className="text-[11px] font-bold text-[#EA580C] whitespace-nowrap">
                                    {r.amount_gnf.toLocaleString("fr-FR")} GNF
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans bg-white/60 p-3 rounded-lg" data-testid="ai-relance-template">{data.relance_template}</pre>
              {recipients.length > 0 && (
                <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1">
                  <Sparkles className="h-3 w-3 text-[#25D366]" />
                  Le message sera personnalisé automatiquement avec le nom du parent destinataire.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
